import { ArtworkResult } from '@/components/w2pFileUploader';
import {
  ProductPrintTechnique,
  SmallDetailsRepresentation,
} from '@midocean/webapps-shared-domain';
import { determineRangeSAPCode } from '@/utils/generatePriceParamsEventPayload';

import {
  CreatePersonalizationForm,
  NewProductPrintPositionForms,
  NewProofForm,
  PersonalizationArtwork,
  ProductVariant,
  ProductVariantImage,
} from '@midocean/webapps-shared-domain';
import { px2mm } from '@/utils';
import {
  PmsColor,
  PrintTechnique,
  ProductPrintPosition,
  ProductPrintData,
} from '@midocean/webapps-shared-domain';

const getPrintTechniqueCode = (
  productPrintData: ProductPrintData,
  printPositionSAPCode: string,
  printTechinqueSAPCode: string
) => {
  return productPrintData.productPrintPositions
    .filter((p) => p.name === printPositionSAPCode)
    .map(({ printTechniques }) =>
      printTechniques.find(
        (pt) => pt.printTechnique.code === printTechinqueSAPCode
      )
    )[0]?.printTechnique.code;
};

const getPrintTechniqueName = (
  languageCode: string,
  productPrintData: ProductPrintData,
  printPositionSAPCode: string,
  printTechinqueSAPCode: string
) => {
  const printTechnique = productPrintData.productPrintPositions
    .filter((p) => p.name === printPositionSAPCode)
    .map(({ printTechniques }) =>
      printTechniques.find(
        (pt) => pt.printTechnique.code === printTechinqueSAPCode
      )
    )[0]?.printTechnique;

  const techniqueName = printTechnique?.nameTranslations.find(
    (nt) => nt.language.code === languageCode
  )?.text;

  return techniqueName === '' || techniqueName === undefined
    ? printTechnique?.nameTranslations.find((nt) => nt.language.code === 'en')
        ?.text
    : techniqueName;
};

const getByPositionId = (
  printPositionId: string,
  variantsByPositions: {
    variantColor: string;
    imageUrl: ProductVariantImage[];
  }[]
) => {
  return variantsByPositions.map((x) => {
    return {
      variantColor: x.variantColor,
      imageUrl:
        x.imageUrl.find((url) => url.printPositionID === printPositionId)
          ?.imageUrl || 'temp url',
    };
  });
};

const logosByPosition = (
  artworkPayloads: ArtworkResult[],
  positionArtworkMap: {
    printPositionSAPCode: string;
    artworkId: string;
    width: number;
    height: number;
    smallDetails: SmallDetailsRepresentation;
  }[],
  printPositionSAPCode: string
) => {
  return artworkPayloads.filter((artwork) =>
    positionArtworkMap.some(
      (pam) =>
        pam.printPositionSAPCode === printPositionSAPCode &&
        pam.artworkId === artwork.personalizationArtworkId
    )
  );
};

const removeClosestColourIfExists = (
  pmsColors: PmsColor[] | null
): PmsColor[] | null => {
  if (pmsColors === null) {
    return null;
  }

  return pmsColors.map((c) => {
    const includes = c.name.includes('Closest Pantone:');

    if (includes) {
      c.name = c.name.split(':')[1].trimStart();
    }

    return c;
  });
};

const constructFullDesignNewProofForm = (
  languageCode: string,
  uploadedArtworksData: ArtworkResult[],
  printPositions: ProductPrintPosition[],
  printTechniques: Map<string, PrintTechnique>,
  positionArtworkMap: {
    printPositionSAPCode: string;
    artworkId: string;
    width: number;
    height: number;
  }[],
  productPrintData: ProductPrintData,
  variantsByPositions: any[],
  instructions: string,
  colorsFromPdpPerPrintPosition: Map<string, number>,
  isFullDesignWithArtworksPathEnabled: boolean,
  isSameLogoForAllPrintPosition: boolean
): NewProofForm => {
  return {
    newProductPrintPositionForms: printPositions.map((u, i) => {
      const dpi =
        positionImageDpi(u?.id, productPrintData.productPrintPositions) || 300;
      const positions = positionArtworkMap.filter((position: any) => {
        return position.printPositionSAPCode === u?.name;
      });

      const { width, height } = calculatePrintArea(
        dpi,
        positions,
        uploadedArtworksData
      );

      const printTechnique = printTechniques.get(u.name);

      if (printTechnique)
        return {
          name: u?.name,
          sapCode: u?.name,
          widthMm: width || 0,
          heightMm: height || 0,
          instructions: instructions,
          numberOfColorsFromPdp: colorsFromPdpPerPrintPosition.get(u.name),
          productPrintTechnique: {
            name: getPrintTechniqueName(
              languageCode,
              productPrintData,
              u?.name,
              printTechnique.printTechnique
                ? printTechnique.printTechnique.code
                : ''
            ),
            sapCode: getPrintTechniqueCode(
              productPrintData,
              u?.name,
              printTechnique.printTechnique
                ? printTechnique.printTechnique.code
                : ''
            ),
            sapRangeCode: determineRangeSAPCode(
              Math.round(width),
              Math.round(height),
              printTechnique
            ),
          } as ProductPrintTechnique,
          printPositionImages: getByPositionId(u?.id, variantsByPositions),
          personalizationTexts: [],
          personalizationArtworks: isFullDesignWithArtworksPathEnabled
            ? uploadedArtworksData
            : [],
        } as unknown as NewProductPrintPositionForms;
    }),
    isSameLogoForAllPosition: isSameLogoForAllPrintPosition,
  } as NewProofForm;
};

const constructFullDesignArtworksNewProofForm = (
  languageCode: string,
  uploadedArtworksData: ArtworkResult[],
  printPositions: ProductPrintPosition[],
  printTechniques: Map<string, PrintTechnique>,
  positionArtworkMap: {
    printPositionSAPCode: string;
    artworkId: string;
    width: number;
    height: number;
  }[],
  productPrintData: ProductPrintData,
  variantsByPositions: any[],
  instructions: string,
  colorsFromPdpPerPrintPosition: Map<string, number>,
  isFullDesignWithArtworksPathEnabled: boolean,
  isSameLogoForAllPrintPosition: boolean
): NewProofForm => {
  return {
    newProductPrintPositionForms: printPositions.map((u, i) => {
      const dpi =
        positionImageDpi(u?.id, productPrintData.productPrintPositions) || 300;
      const positions = positionArtworkMap.filter((position: any) => {
        return position.printPositionSAPCode === u?.name;
      });

      const { width, height } = calculatePrintArea(
        dpi,
        positions,
        uploadedArtworksData
      );

      const printTechnique = printTechniques.get(u.name);

      if (printTechnique)
        return {
          name: u?.name,
          sapCode: u?.name,
          widthMm: width || 0,
          heightMm: height || 0,
          instructions: instructions,
          numberOfColorsFromPdp: colorsFromPdpPerPrintPosition.get(u.name),
          productPrintTechnique: {
            name: getPrintTechniqueName(
              languageCode,
              productPrintData,
              u?.name,
              printTechnique.printTechnique
                ? printTechnique.printTechnique.code
                : ''
            ),
            sapCode: getPrintTechniqueCode(
              productPrintData,
              u?.name,
              printTechnique.printTechnique
                ? printTechnique.printTechnique.code
                : ''
            ),
            sapRangeCode: determineRangeSAPCode(
              Math.round(width),
              Math.round(height),
              printTechnique
            ),
          } as ProductPrintTechnique,
          printPositionImages: getByPositionId(u?.id, variantsByPositions),
          personalizationTexts: [],
          personalizationArtworks: [
            {
              id: uploadedArtworksData.find((x) => x.position === u.name)
                ?.personalizationArtworkId,
            },
          ],
        } as unknown as NewProductPrintPositionForms;
    }),
    isSameLogoForAllPosition: isSameLogoForAllPrintPosition,
  } as NewProofForm;
};

const constructLogoOnlyNewProofForm = (
  languageCode: string,
  uploadedArtworksData: ArtworkResult[],
  printPositions: ProductPrintPosition[],
  printTechniques: Map<string, PrintTechnique>,
  positionArtworkMap: {
    printPositionSAPCode: string;
    artworkId: string;
    width: number;
    height: number;
    smallDetails: SmallDetailsRepresentation;
  }[],
  productPrintData: ProductPrintData,
  variantsByPositions: any[],
  instructions: string,
  colorsFromPdpPerPrintPosition: Map<string, number>
): NewProofForm => {
  return {
    newProductPrintPositionForms: printPositions.map((u, i) => {
      const dpi =
        positionImageDpi(u?.id, productPrintData.productPrintPositions) || 300;
      const positions = positionArtworkMap.filter((position: any) => {
        return position.printPositionSAPCode === u?.name;
      });

      const { width, height } = calculatePrintArea(
        dpi,
        positions,
        uploadedArtworksData
      );

      const printTechnique = printTechniques.get(u.name);

      if (printTechnique)
        return {
          name: u?.name || 'temp name',
          sapCode: u?.name,
          widthMm: width || 0,
          heightMm: height || 0,
          instructions: instructions,
          numberOfColorsFromPdp: colorsFromPdpPerPrintPosition.get(u.name) ?? 0,
          productPrintTechnique: {
            name:
              getPrintTechniqueName(
                languageCode,
                productPrintData,
                u?.name,
                printTechnique.printTechnique
                  ? printTechnique.printTechnique.code
                  : ''
              ) || 'temp name',
            sapCode: getPrintTechniqueCode(
              productPrintData,
              u?.name,
              printTechnique.printTechnique
                ? printTechnique.printTechnique.code
                : ''
            ),
            sapRangeCode: determineRangeSAPCode(
              Math.round(width),
              Math.round(height),
              printTechnique
            ),
          } as ProductPrintTechnique,
          printPositionImages: getByPositionId(u?.id, variantsByPositions),
          personalizationTexts: [], // TODO pending
          personalizationArtworks: logosByPosition(
            uploadedArtworksData,
            positionArtworkMap,
            u?.name
          ).map((artwork) => {
            return {
              id: artwork.personalizationArtworkId,
              pmsColorsinPngFile: removeClosestColourIfExists(
                artwork.pmsColorsInPngFile
              ),
              height: generateHeight(positionArtworkMap[i].height, artwork),
              width: generateWidth(positionArtworkMap[i].width, artwork),
              pngHorizontalResolutionDPIs: artwork.pngHorizontalResolutionDPIs,
              pngVerticalResolutionDPIs: artwork.pngVerticalResolutionDPIs,
              isFileConvertedToCmykPdf: artwork.isFileConvertedToCmykPdf,
              areCmykValuesCorrect: artwork.areCmykValuesCorrect,
              smallDetails:
                positionArtworkMap[i].smallDetails != null
                  ? {
                      positiveLoss:
                        positionArtworkMap[i].smallDetails.positiveLoss,
                      negativeLoss:
                        positionArtworkMap[i].smallDetails.negativeLoss,
                      approvedAt: positionArtworkMap[i].smallDetails.approvedAt,
                      status: positionArtworkMap[i].smallDetails.status,
                      calculatedQuality:
                        positionArtworkMap[i].smallDetails.calculatedQuality,
                    }
                  : null,
            } as unknown as PersonalizationArtwork;
          }),
        } as unknown as NewProductPrintPositionForms;
    }),
  } as NewProofForm;
};

const positionImageDpi = (
  positionId: string,
  productPrintPositions: ProductPrintPosition[]
) => {
  return productPrintPositions.find((p) => p.id === positionId)
    ?.originalImageWebDpi;
};

const calculatePrintArea = (
  dpi: number,
  positions: {
    printPositionSAPCode: string;
    artworkId: string;
    width: number;
    height: number;
  }[],
  artworks: ArtworkResult[]
) => {
  const artwork = artworks.filter((artwork: ArtworkResult) => {
    return positions.find(
      (pos: any) => artwork.personalizationArtworkId === pos.artworkId
    );
  })[0];

  const width = positions.find(
    (p) => p.artworkId === artwork.personalizationArtworkId
  )?.width;
  const height = positions.find(
    (p) => p.artworkId === artwork.personalizationArtworkId
  )?.height;

  return {
    width: width ? width : px2mm(artwork?.pngWidthPixels, dpi),
    height: height ? height : px2mm(artwork?.pngHeightPixels, dpi),
  };
};

const generateHeight = (height: number, artwork: ArtworkResult) => {
  return height === 0
    ? px2mm(artwork.pngHeightPixels, artwork.pngVerticalResolutionDPIs)
    : height;
};

const generateWidth = (width: number, artwork: ArtworkResult) => {
  return width === 0
    ? px2mm(artwork.pngWidthPixels, artwork.pngHorizontalResolutionDPIs)
    : width;
};

export const constructLogoOnlyNewPersonalizationForm = (
  languageCode: string,
  sapCustomerId: string,
  masterProductSku: string,
  uploadedFileData: ArtworkResult[],
  retrievedProductData: ProductPrintData,
  productPrintPositions: ProductPrintPosition[],
  productPrintTechniques: Map<string, PrintTechnique>,
  productVariants: ProductVariant[],
  variantsByPositions: any[],
  positionArtworkMap: {
    printPositionSAPCode: string;
    artworkId: string;
    width: number;
    height: number;
    smallDetails: SmallDetailsRepresentation;
  }[],
  sendMailToCustomerCare: boolean,
  instructions: string,
  colorsFromPdpPerPrintPosition: Map<string, number>,
  orderNumber?: string,
  orderLineId?: string,
  customerCompanyLogoUrl?: string
): CreatePersonalizationForm => {
  const newProofForm = constructLogoOnlyNewProofForm(
    languageCode,
    uploadedFileData,
    productPrintPositions,
    productPrintTechniques,
    positionArtworkMap,
    retrievedProductData,
    variantsByPositions,
    instructions,
    colorsFromPdpPerPrintPosition
  );

  return {
    externalCustomerId: sapCustomerId,
    productSKU: masterProductSku,
    productName: retrievedProductData.name,
    customerCompanyLogoUrl: customerCompanyLogoUrl,
    contactLanguageCode: languageCode,
    creator: '',
    userId: '',
    initialInstructions: '',
    newProofForm: newProofForm,
    isConcept: true,
    isDirectApproval: false,
    orderNumber: orderNumber,
    orderLineId: orderLineId,
    proofType: 'OnlineSimplePersonalization',
    productVariants: productVariants,
    sendMailToCustomerCare: sendMailToCustomerCare,
  } as CreatePersonalizationForm;
};

export const constructFullDesignNewPersonalizationForm = (
  languageCode: string,
  sapCustomerId: string,
  masterProductSku: string,
  uploadedFileData: ArtworkResult[],
  retrievedProductData: ProductPrintData,
  productPrintPositions: ProductPrintPosition[],
  productPrintTechniques: Map<string, PrintTechnique>,
  productVariants: ProductVariant[],
  variantsByPositions: any[],
  positionArtworkMap: {
    printPositionSAPCode: string;
    artworkId: string;
    width: number;
    height: number;
  }[],
  sendMailToCustomerCare: boolean,
  instructions: string,
  colorsFromPdpPerPrintPosition: Map<string, number>,
  isFullDesignWithArtworksPathEnabled: boolean,
  isSameLogoForAllPrintPosition: boolean,
  orderNumber?: string,
  orderLineId?: string
): CreatePersonalizationForm => {
  const newProofForm = isFullDesignWithArtworksPathEnabled
    ? constructFullDesignArtworksNewProofForm(
        languageCode,
        uploadedFileData,
        productPrintPositions,
        productPrintTechniques,
        positionArtworkMap,
        retrievedProductData,
        variantsByPositions,
        instructions,
        colorsFromPdpPerPrintPosition,
        isFullDesignWithArtworksPathEnabled,
        isSameLogoForAllPrintPosition
      )
    : constructFullDesignNewProofForm(
        languageCode,
        uploadedFileData,
        productPrintPositions,
        productPrintTechniques,
        positionArtworkMap,
        retrievedProductData,
        variantsByPositions,
        instructions,
        colorsFromPdpPerPrintPosition,
        isFullDesignWithArtworksPathEnabled,
        isSameLogoForAllPrintPosition
      );

  return {
    externalCustomerId: sapCustomerId,
    productSKU: masterProductSku,
    productName: retrievedProductData.name,
    customerCompanyLogoUrl: '',
    contactLanguageCode: languageCode,
    creator: '',
    userId: '',
    initialInstructions: '',
    newProofForm: newProofForm,
    isConcept: true,
    isDirectApproval: false,
    orderNumber: orderNumber,
    orderLineId: orderLineId,
    proofType: isFullDesignWithArtworksPathEnabled
      ? 'FullDesignWithArtworks'
      : 'FullDesign',
    productVariants: productVariants,
    sendMailToCustomerCare: sendMailToCustomerCare,
  } as CreatePersonalizationForm;
};
